import type { ExternalUserAccessControlInterface } from '@zaoblako/shared/modules/security/dtos/external-user/external-user-access-control.dto';
import type { ExternalUserFindInterface } from '@zaoblako/shared/modules/security/dtos/external-user/external-user-find.dto';
import type { PersonalAccountMenuItemFindInterface } from '@zaoblako/shared/modules/security/dtos/personal-account/menu-item/personal-account-menu-item-find.dto';

import { SpinnerComponent } from '@zaoblako/web/shared/dumbs/spinner/spinner.component';
import { PersonalAccountMenuService } from '@zaoblako/web/core/services/lk/personal-account-menu.service';

import { CardModule } from 'primeng/card';
import { InputSwitchModule } from 'primeng/inputswitch';
import { MessagesModule } from 'primeng/messages';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import {
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  Input,
  Output,
  ChangeDetectorRef,
  type OnInit
} from '@angular/core';

interface AccessRightItemInterface {
  visible?: boolean;
  key?: keyof ExternalUserAccessControlInterface;
  label: string;
  hint?: string;
  group?: boolean;
  children?: AccessRightItemInterface[];
}

const DEFAULT_ACCESS_CONTROL: ExternalUserAccessControlInterface = {
  admin: false,
  externalUser: false,
  hdTask: false,
  cctvTransitTask: false,
  cctvResidentCar: false,
  pacsPassageTask: false,
  pacsPassageOrder: false
};

@Component({
  standalone: true,
  selector: 'crm-lk-eu-access-control-widget',
  templateUrl: './lk-eu-access-control-widget.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    CommonModule,
    CardModule,
    InputSwitchModule,
    FormsModule,
    SpinnerComponent,
    MessagesModule
  ]
})
export class LkEuAccessControlWidgetComponent implements OnInit {
  @Input() public loading = false;
  @Input() public user: ExternalUserFindInterface;
  @Input() public currentUserHasEditPermission = true;
  @Input() public withCard = true;
  @Input() public styleClass: string;

  @Output() public accessChange = new EventEmitter<ExternalUserFindInterface>();

  public get userAccessControl(): ExternalUserAccessControlInterface {
    if (!this.user) {
      return { ...DEFAULT_ACCESS_CONTROL };
    }
    if (!this.user.accessControl) {
      this.user.accessControl = { ...DEFAULT_ACCESS_CONTROL };
    }
    return this.user.accessControl;
  }

  public get isAdmin(): boolean {
    return this.userAccessControl?.admin;
  }

  public accessRights: AccessRightItemInterface[] = [];

  public constructor(
    private readonly personalAccountMenuService: PersonalAccountMenuService,
    private readonly changeDetectorRef: ChangeDetectorRef
  ) {}

  public ngOnInit(): void {
    this.personalAccountMenuService
      .getMenu()
      .subscribe({
        next: ({ items }) => {
          this.buildMenu(items);
          this.changeDetectorRef.markForCheck();
        },
        error: () => {
          this.buildMenu({});
          this.changeDetectorRef.markForCheck();
        }
      });
  }

  public onAccessChange(user: ExternalUserFindInterface) {
    if (user) {
      this.accessChange.emit(user);
    }
  }

  private buildMenu(settings: Record<string, PersonalAccountMenuItemFindInterface>) {
    this.accessRights = [
      {
        key: 'externalUser',
        label: 'Сотрудники',
        visible: settings?.EXTERNAL_USER?.enabled,
        hint:
          'Создание и редактирование профилей, блокировка учетной записи, ' +
          'настройка прав для всех сотрудников, а еще выдача доступа в Личный кабинет'
      },
      {
        key: 'hdTask',
        label: 'Заявки',
        visible: settings?.HD_TASK?.enabled,
        hint: 'Создание заявки и редактирование ее реквизитов'
      },
      {
        label: 'Паркинг',
        group: true,
        visible: settings?.PARKING?.enabled,
        children: [
          {
            key: 'cctvTransitTask',
            label: 'Гостевой проезд',
            visible: settings?.PARKING?.children?.TRANSIT_TASK?.enabled,
            hint: 'Создание заявки на гостевой проезд и редактирование ее реквизитов в статусе "Новая"'
          },
          {
            key: 'cctvResidentCar',
            label: 'Постоянные ТС',
            visible: settings?.PARKING?.children?.RESIDENT_CAR?.enabled,
            hint: 'Создание, редактирование и удаление постоянных ТС'
          }
        ]
      },
      {
        label: 'Пропуска',
        group: true,
        visible: settings?.PACS?.enabled,
        children: [
          {
            key: 'pacsPassageTask',
            label: 'Приглашение гостя',
            visible: settings?.PACS?.children?.PASSAGE_TASK?.enabled,
            hint: 'Создание заявки по приглашению гостя и редактирование ее реквизитов в статусе "Новая", а еще повтор заявки'
          },
          {
            key: 'pacsPassageOrder',
            label: 'Заказ пропусков',
            visible: settings?.PACS?.children?.PASSAGE_ORDER?.enabled,
            hint: 'Создание заявки по оформлению постоянных пропусков для сотрудников и редактирование ее реквизитов в статусе "Новая"'
          }
        ]
      }
    ];
  }
}
